import { ThemeConfig } from 'antd';

enum FontSize {
  XS = 12,
  SM = 14,
  MD = 16,
  LG = 18,
  XL = 24,
}

enum Color {
  NONE = 'transparent',
  DARK = '#0d1929',
  LIGHT = '#ffffff',
  ERROR = '#f04438',
  ERROR_LIGHTEST = '#feedec',
  WARNING = '#dc6803',
  WARNING_LIGHTEST = '#ffeedf',
  SUCCESS = '#4eb94a',
  SUCCESS_LIGHTEST = '#eef7ed',
  PRIMARY = '#4eb94a',
  PRIMARY_LIGHTEST = '#f4fbf4',
  PRIMARY_700 = '#2d6e2b',
  GRAY_50 = '#fcfcfd',
  GRAY_100 = '#f9fafb',
  GRAY_150 = '#f1f4f8',
  GRAY_200 = '#e6edf3',
  GRAY_300 = '#d0d5dd',
  GRAY_400 = '#91a4b8',
  GRAY_500 = '#5f7389',
  GRAY_600 = '#4b6a94',
  GRAY_700 = '#2e4258',
  GRAY_800 = '#2a3c53',
  GRAY_900 = '#1d2939',
}

enum Gap {
  XS = 4,
  SM = 8,
  MD = 16,
  LG = 24,
}

const themeConfig: ThemeConfig = {
  token: {
    fontFamily: '"Inter", sans-serif',
    fontSize: FontSize.SM,
    fontSizeLG: FontSize.MD,
    fontSizeHeading1: FontSize.XL,
    fontSizeHeading2: FontSize.LG,
    fontSizeHeading3: FontSize.MD,
    fontWeightStrong: 600,
    colorText: Color.DARK,
    colorLink: Color.PRIMARY,
    colorError: Color.ERROR,
    colorErrorBg: Color.ERROR_LIGHTEST,
    colorWarning: Color.WARNING,
    colorWarningBg: Color.WARNING_LIGHTEST,
    colorSuccess: Color.SUCCESS,
    colorSuccessBg: Color.SUCCESS_LIGHTEST,
    colorPrimary: Color.PRIMARY,
    colorPrimaryBg: Color.PRIMARY_LIGHTEST,
    colorBorder: Color.GRAY_200,
    borderRadiusLG: 8,
  },
  components: {
    Alert: {
      defaultPadding: Gap.MD,
      lineWidth: 0,
      marginXS: Gap.MD,
    },
    Breadcrumb: {
      colorBgTextHover: Color.NONE,
      itemColor: Color.GRAY_500,
      lastItemColor: Color.GRAY_500,
      linkColor: Color.GRAY_500,
      linkHoverColor: Color.PRIMARY,
      separatorColor: Color.GRAY_300,
    },
    Button: {
      fontSizeLG: FontSize.SM,
      fontWeight: 500,
      defaultShadow: 'none',
      dangerShadow: 'none',
      primaryShadow: 'none',
    },
    DatePicker: {
      fontSizeLG: FontSize.SM,
    },
    Descriptions: {
      colonMarginLeft: 0,
      colonMarginRight: Gap.SM,
      itemPaddingBottom: Gap.MD,
      itemPaddingEnd: Gap.MD,
      colorTextTertiary: Color.GRAY_500,
    },
    Drawer: {
      colorBgElevated: Color.GRAY_50,
      footerPaddingBlock: Gap.MD,
      footerPaddingInline: Gap.LG,
    },
    Form: {
      itemMarginBottom: Gap.LG,
    },
    Input: {
      fontSizeLG: FontSize.SM,
    },
    Layout: {
      bodyBg: Color.NONE,
      headerBg: Color.GRAY_50,
      headerHeight: 60,
      headerPadding: 0,
    },
    Menu: {
      colorBgContainer: Color.NONE,
      itemPaddingInline: Gap.MD,
      activeBarHeight: 0,
      activeBarBorderWidth: 0,
    },
    Pagination: {
      fontWeightStrong: 500,
      colorBgTextHover: Color.PRIMARY_LIGHTEST,
      colorBgTextActive: Color.PRIMARY_LIGHTEST,
      colorText: Color.GRAY_700,
      colorPrimary: Color.PRIMARY_700,
      itemSize: 40,
      itemActiveBg: Color.PRIMARY_LIGHTEST,
      lineWidth: 0,
      marginXS: Gap.XS,
    },
    Progress: {
      defaultColor: Color.PRIMARY,
    },
    Select: {
      fontSizeLG: FontSize.SM,
    },
    Table: {
      fontWeightStrong: 500,
      colorBgContainer: Color.NONE,
      colorText: Color.GRAY_500,
      borderColor: Color.GRAY_200,
      headerBg: Color.NONE,
      headerColor: Color.GRAY_500,
      headerBorderRadius: 0,
      headerSplitColor: Color.NONE,
      rowHoverBg: Color.PRIMARY_LIGHTEST,
      padding: Gap.LG,
    },
    Tag: {
      defaultBg: Color.GRAY_150,
    },
    Typography: {
      colorTextDescription: Color.GRAY_500,
    },
  },
};

export default themeConfig;
